import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Package, Info } from 'lucide-react';
import Layout from '../Layout';
import { SaleCartManager } from '../../helpers/SaleCartManager';
import { showToast } from '../../helpers/customToast';
import { saveAddress, checkLoggedIn, getCurrentUserData, updateUser, getCurrentUserAddress, createGuestAccount } from '../../helpers/user';
import { generatePassword, countriesList } from '../../helpers/tools';
import { createSaleOrder, createSalePaymentLink } from '../../helpers/saleOrder';
import DeliverySelection from '../DeliverySelection';
import { getData } from '../../helpers/ApiSettings';
import {
  calculateCartWeight,
  detectShippingZone,
  getColissimoRate,
  COLISSIMO_MAX_WEIGHT_KG,
} from '../../helpers/colissimo';

const SaleCheckoutPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedRelayPoint, setSelectedRelayPoint] = useState(null);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [colissimoWithSignature, setColissimoWithSignature] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    postal_code: '',
    comments: '',
    country: ''
  });

  useEffect(() => {
    const initializeCheckout = async () => {
      await checkAuthentication();
      fetchCartItems();
    };

    initializeCheckout();
  }, []);

  const validateCoupon = async (code) => {
    try {
      setIsValidatingCoupon(true);
      const response = await getData(`/coupons?filters[code][$eq]=${code}&populate=*`);

      if (!response.data.data?.[0]) {
        showToast('Code promo invalide', 'error');
        return;
      }

      const coupon = response.data.data[0];

      // Check if coupon is active
      if (!coupon.is_active) {
        showToast('Ce code promo n\'est plus valide', 'error');
        return;
      }

      // Check if coupon is applicable to sales
      if (coupon.applicable_to_sales === false) {
        showToast('Ce code promo n\'est pas valide pour les produits à la vente', 'error');
        return;
      }

      // Check validity period
      const now = new Date();
      const validFrom = new Date(coupon.valid_from);
      const validUntil = new Date(coupon.valid_until);

      if (now < validFrom || now > validUntil) {
        showToast('Ce code promo a expiré', 'error');
        return;
      }

      // Check minimum amount
      if (coupon.min_amount && totalPrice < coupon.min_amount) {
        showToast(`Le montant minimum d'achat pour ce code est de ${coupon.min_amount}€`, 'error');
        return;
      }

      // Check if authorised for only one user
      if (coupon.user) {
        if (isAuthenticated) {
          if (currentUserData.id !== coupon.user.id) {
            showToast(`Vous ne pouvez pas utiliser ce coupon.`, 'error');
            return;
          }
        } else {
          showToast(`Merci de vous connecter afin de vérifier ce coupon.`, 'error');
          return;
        }
      }

      setAppliedCoupon(coupon);
      showToast('Code promo appliqué avec succès', 'success');
    } catch (error) {
      console.error('Error validating coupon:', error);
      showToast('Erreur lors de la validation du code promo', 'error');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;

    let discount = 0;
    if (appliedCoupon.is_percentage) {
      discount = (totalPrice * appliedCoupon.amount) / 100;
    } else {
      discount = appliedCoupon.amount;
    }

    return Math.min(discount, totalPrice + deliveryPrice);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountrySearch = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, country: value }));

    if (value.length > 1) {
      const results = await countriesList(value);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  // Colissimo: derived values recomputed each render.
  const cartWeight = calculateCartWeight(cartItems);
  const colissimoZone = detectShippingZone(formData.postal_code);
  // Colissimo: éligible dès qu'au moins un produit a un poids renseigné.
  // Les produits sans poids sont ignorés du calcul (warning UI seulement).
  const colissimoRate =
    cartWeight.weightedCount > 0 &&
    colissimoZone === 'intra-dom' &&
    cartWeight.totalKg > 0 &&
    cartWeight.totalKg <= COLISSIMO_MAX_WEIGHT_KG
      ? getColissimoRate(cartWeight.totalKg, 'intra-dom', colissimoWithSignature)
      : null;
  const colissimoAvailable = colissimoRate != null;

  // If user picked Colissimo but it becomes unavailable (postal code changed,
  // cart updated, etc.), reset their selection so they pick a valid method.
  useEffect(() => {
    if (selectedDeliveryMethod !== 'colissimo') return;
    if (!colissimoAvailable) {
      setSelectedDeliveryMethod('');
      setDeliveryPrice(0);
    } else {
      setDeliveryPrice(colissimoRate.price);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colissimoAvailable, colissimoRate?.price]);

  const handleDeliveryMethodChange = (method) => {
    setSelectedDeliveryMethod(method);
    // Set delivery price based on cart total and method
    if (method === 'home_delivery') {
      setDeliveryPrice(SaleCartManager.isEligibleForFreeDelivery() ? 0 : 5.99);
    } else if (method === 'colissimo') {
      setDeliveryPrice(colissimoRate ? colissimoRate.price : 0);
    } else {
      setDeliveryPrice(0); // Reset for relay point selection
    }
  };

  const handleColissimoSignatureChange = (withSig) => {
    setColissimoWithSignature(withSig);
    // Price update is handled by the useEffect above when colissimoRate recomputes.
  };

  const handleZoneChange = (zone) => {
    if (zone) {
      setSelectedZone(zone.id);
      setDeliveryPrice(SaleCartManager.isEligibleForFreeDelivery() ? 0 : (zone.price || 5.99));
      return;
    }
    setSelectedZone('');
  };

  const handleRelayPointChange = (point) => {
    setSelectedRelayPoint(point);
    if (point) {
      setDeliveryPrice(SaleCartManager.isEligibleForFreeDelivery() ? 0 : (point.price || 0));
    }
  };

  const selectCountry = (country) => {
    setFormData(prev => ({ ...prev, country: country }));
    setSearchResults([]);
  };

  const checkAuthentication = async () => {
    try {
      const authStatus = await checkLoggedIn();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        const userData = await getCurrentUserData();
        let userAddress;
        if (userData.user_address?.id) {
          userAddress = await getCurrentUserAddress(userData.user_address.documentId);
        }
        setCurrentUserData(userData);

        setFormData(prevFormData => ({
          ...prevFormData,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          address_1: userAddress?.address_1 || '',
          address_2: userAddress?.address_2 || '',
          city: userAddress?.city || '',
          postal_code: userAddress?.postal_code || '',
          country: userAddress?.country || '',
          phone: userAddress?.phone || ''
        }));
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      showToast('Échec de la vérification du statut d\'authentification', 'error');
    }
  };

  const fetchCartItems = () => {
    try {
      const items = SaleCartManager.getCart();
      const total = SaleCartManager.getTotalPrice();

      // Redirect to cart if empty
      if (items.length === 0) {
        navigate('/vente/panier');
        return;
      }

      setCartItems(items);
      setTotalPrice(total);

      // Set initial delivery price
      setDeliveryPrice(SaleCartManager.isEligibleForFreeDelivery() ? 0 : 5.99);
    } catch (error) {
      console.error('Error fetching cart items:', error);
      showToast('Échec du chargement des articles du panier', 'error');
    }
  };

  const handleGuestCheckout = async () => {
    try {
      const password = generatePassword();
      const userData = {
        username: formData.email,
        password,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name
      };

      const newUser = await createGuestAccount(userData);
      if (!newUser.success) {
        throw new Error('Failed to create guest account');
      }
      setCurrentUserData(newUser.user);

      return newUser.user.id;
    } catch (error) {
      console.error('Guest checkout failed:', error);
      throw new Error('Failed to create account for checkout');
    }
  };

  const handleAddress = async (userId) => {
    const addressData = {
      address_1: formData.address_1,
      address_2: formData.address_2,
      country: formData.country,
      postal_code: formData.postal_code,
      city: formData.city,
      phone: formData.phone,
      user: userId
    };

    try {
      await saveAddress({ data: addressData });
    } catch (error) {
      console.error('Failed to save address:', error);
      throw new Error('Failed to save shipping address');
    }
  };

  const validateForm = () => {
    const requiredFields = {
      first_name: 'Prénom',
      last_name: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      address_1: 'Adresse',
      country: 'Pays',
      city: 'Ville',
      postal_code: 'Code Postal'
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]?.trim()) {
        throw new Error(`Le champ "${label}" est requis.`);
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Veuillez saisir une adresse e-mail valide');
    }

    if (!selectedDeliveryMethod) {
      throw new Error('Veuillez sélectionner un mode de livraison');
    }

    if (selectedDeliveryMethod === 'home_delivery' && !selectedZone) {
      throw new Error('Veuillez sélectionner une zone de livraison');
    }

    if (selectedDeliveryMethod === 'relay_point' && !selectedRelayPoint) {
      throw new Error('Veuillez sélectionner un point relais');
    }

    if (selectedDeliveryMethod === 'colissimo' && !colissimoRate) {
      throw new Error('Livraison Colissimo indisponible pour cette adresse ou ce panier');
    }
  };

  const processOrder = async (orderData) => {
    try {
      // No stock validation since we're allowing backorders

      // Create the sale order
      const orderResult = await createSaleOrder({
        ...orderData,
        status: 'pending'
      });

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create order');
      }

      // Create Stripe payment link
      const paymentLinkResult = await createSalePaymentLink({
        orderId: orderResult.orderId,
        documentId: orderResult.documentId,
        totalAmount: orderData.totalAmount,
        customerEmail: formData.email,
        firstName: formData.first_name,
        lastName: formData.last_name
      });

      return {
        orderId: orderResult.orderId,
        paymentUrl: paymentLinkResult.url
      };
    } catch (error) {
      console.error('Error in processOrder:', error);
      throw new Error(error.message || 'Failed to process order');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      validateForm();
      let userId;

      if (!isAuthenticated) {
        userId = await handleGuestCheckout();
      } else {
        userId = currentUserData.id;
        await updateUser(userId, {
          first_name: formData.first_name,
          last_name: formData.last_name
        });
      }
      await handleAddress(userId);

      const orderData = {
        userId,
        items: cartItems,
        shippingAddress: {
          address_1: formData.address_1,
          address_2: formData.address_2,
          country: formData.country,
          postal_code: formData.postal_code,
          city: formData.city,
          phone: formData.phone,
        },
        delivery: {
          method: selectedDeliveryMethod,
          price: deliveryPrice,
          ...(selectedDeliveryMethod === 'colissimo' && {
            colissimo: {
              weight_kg: cartWeight.totalKg,
              zone: colissimoZone,
              with_signature: colissimoWithSignature,
            },
          }),
        },
        subtotal: totalPrice,
        totalAmount: (totalPrice + deliveryPrice) - calculateDiscount(),
        comments: formData.comments,
        coupon: appliedCoupon ? {
          id: appliedCoupon.id,
          code: appliedCoupon.code,
          discount: calculateDiscount()
        } : null,
        userDetails: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          address_1: formData.address_1,
          address_2: formData.address_2,
          country: formData.country,
          postal_code: formData.postal_code,
          city: formData.city,
          phone: formData.phone,
        },
        relayPoint: selectedDeliveryMethod === 'relay_point' ? selectedRelayPoint.id : null,
      };

      const result = await processOrder(orderData);

      // Redirect to Stripe payment page
      window.location.href = result.paymentUrl;
    } catch (error) {
      console.error('Checkout failed:', error);
      showToast(error.message || 'Une erreur est survenue, merci de réessayer.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="container my-5">
        <h1 className="mb-4">Finaliser ma commande</h1>
        
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/vente">Boutique</Link></li>
            <li className="breadcrumb-item"><Link to="/vente/panier">Panier</Link></li>
            <li className="breadcrumb-item active" aria-current="page">Commande</li>
          </ol>
        </nav>

        {!isAuthenticated && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">Déjà client ?</h5>
                  <p className="text-muted mb-0">
                    Connectez-vous pour un paiement plus rapide et suivre vos commandes
                  </p>
                </div>
                <div className="col-md-4 text-end">
                  <Link
                    to="/connexion?redirect=vente/checkout"
                    className="btn btn-outline-primary"
                  >
                    Se connecter
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-md-8">
            <form onSubmit={handleSubmit}>
              <div className="card mb-4 overflow-visible">
                <div className="card-body">
                  <h3 className="card-title">Informations Personnelles</h3>
                  {isAuthenticated && (
                    <p className='mb-3 text-secondary'><i>Votre compte sera mis à jour avec ces informations.</i></p>
                  )}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="last_name" className="form-label">Nom <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="first_name" className="form-label">Prénom <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label">E-mail <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        readOnly={isAuthenticated}
                        disabled={isAuthenticated}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label">Téléphone <span className="text-danger">*</span></label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="address" className="form-label">Adresse <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        id="address_1"
                        name="address_1"
                        value={formData.address_1}
                        onChange={handleInputChange}
                        required
                      />
                      <input
                        type="text"
                        className="form-control"
                        id="address_2"
                        name="address_2"
                        value={formData.address_2}
                        onChange={handleInputChange}
                        placeholder="Complément d'adresse (optionnel)"
                      />
                    </div>
                    <div className="col-md-12">
                      <label htmlFor="country" className="form-label">Pays <span className="text-danger">*</span></label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control"
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleCountrySearch}
                          autoComplete="off"
                          required
                        />
                        {searchResults.length > 0 && (
                          <div className="position-absolute w-100 mt-1 bg-light border rounded shadow-sm" style={{ zIndex: 1000, height: 100, overflow: 'scroll' }}>
                            {searchResults.map((country, index) => (
                              <div
                                key={index}
                                className="p-2 cursor-pointer hover:bg-white"
                                style={{ cursor: 'pointer' }}
                                onClick={() => selectCountry(country.name_fr)}
                              >
                                {country.name_fr}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="city" className="form-label">Ville <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="postal_code" className="form-label">Code Postal <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Commentaires <small className='text-secondary'><i>(optionnel)</i></small></h3>
                  <p>Si vous avez des demandes particulières merci de les indiquer ici.</p>
                  <textarea 
                    className="form-control" 
                    id="comments" 
                    name="comments" 
                    rows="3"
                    value={formData.comments}
                    onChange={handleInputChange}
                    placeholder="Commentaires sur votre commande..."
                  ></textarea>
                </div>
              </div>
            </form>
          </div>

          <div className="col-md-4">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Récapitulatif</h3>
                {cartItems.map((item, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="my-0">{item.product.title}</h6>
                      <small className="text-muted">
                        <Package size={14} className="me-1" />
                        {item.quantity} x {item.price}€
                        {item.product.bulk_price && item.product.bulk_quantity && item.quantity >= item.product.bulk_quantity && (
                          <span className="text-success"> (prix dégressif)</span>
                        )}
                      </small>
                    </div>
                    <span className="text-muted">{(item.price * item.quantity).toFixed(2)}€</span>
                  </div>
                ))}

                <hr className="my-4" />
                
                <div className="mb-3">
                  <label htmlFor="coupon" className="form-label">Code promo</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="coupon"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Entrez votre code"
                      disabled={isValidatingCoupon || appliedCoupon}
                    />
                    {!appliedCoupon ? (
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={() => validateCoupon(couponCode)}
                        disabled={!couponCode || isValidatingCoupon}
                      >
                        {isValidatingCoupon ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : 'Appliquer'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-danger"
                        type="button"
                        onClick={() => {
                          setAppliedCoupon(null);
                          setCouponCode('');
                        }}
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                  {appliedCoupon && (
                    <small className="text-success">
                      Code promo appliqué : {appliedCoupon.is_percentage ? `${appliedCoupon.amount}%` : `${appliedCoupon.amount}€`} de réduction
                    </small>
                  )}
                </div>

                <hr className="my-4" />
                
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Sous-total</span>
                  <span>{totalPrice.toFixed(2)}€</span>
                </div>
                
                {deliveryPrice > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Frais de livraison</span>
                    <span>{deliveryPrice.toFixed(2)}€</span>
                  </div>
                )}
                
                {appliedCoupon && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Réduction</span>
                    <span className="text-success">-{calculateDiscount().toFixed(2)}€</span>
                  </div>
                )}
                
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="m-0">Total</h5>
                  <h5 className="m-0">{((totalPrice + deliveryPrice) - calculateDiscount()).toFixed(2)}€</h5>
                </div>
              </div>
            </div>

            <DeliverySelection
              selectedMethod={selectedDeliveryMethod}
              selectedZone={selectedZone}
              selectedRelayPoint={selectedRelayPoint}
              deliveryPrice={deliveryPrice}
              onMethodChange={handleDeliveryMethodChange}
              onZoneChange={handleZoneChange}
              onRelayPointChange={handleRelayPointChange}
              colissimoEnabled={true}
              colissimoAvailable={colissimoAvailable}
              colissimoRate={colissimoRate}
              colissimoCartWeight={cartWeight}
              colissimoZone={colissimoZone}
              colissimoWithSignature={colissimoWithSignature}
              onColissimoSignatureChange={handleColissimoSignatureChange}
            />

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Mode de paiement</h3>
                <div className="d-flex align-items-center">
                  <input type="radio" defaultChecked className="me-2" />
                  <span>Carte bancaire (Stripe)</span>
                  <CreditCard size={18} className="ms-2" />
                </div>
              </div>
            </div>

            <div className="form-check mb-4">
              <input
                type="checkbox"
                name="acceptance"
                id="acceptance"
                required
                className="form-check-input"
              />
              <label htmlFor="acceptance" className="form-check-label">
                J'accepte les <a href="/page/cgv" target="_blank">Conditions Générales</a> <span className="text-danger">*</span>
              </label>
            </div>

            <button 
              type="submit" 
              onClick={handleSubmit} 
              className="btn btn-primary btn-lg w-100"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Traitement...
                </>
              ) : (
                'Commander & Payer'
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SaleCheckoutPage;